<!-- eslint-disable vue/multi-word-component-names -->
<template>
    <form class="el-form" :class="className" @submit.prevent="handleSubmit">
        <slot />
    </form>
</template>

<script setup>
import { reactive, computed, toRefs, watch, nextTick, provide } from 'vue';

const props = defineProps({
    inlineMessage: {
        type: Boolean,
        default: true,
    },
    model: {
        type: Object,
        default: () => ({}),
    },
    rules: {
        type: Object,
        default: () => ({}),
    },
    loading: {
        type: Boolean,
        default: false,
    },
    showMessage: {
        type: Boolean,
        default: true,
    },
    validateOnRuleChange: {
        type: Boolean,
        default: true,
    },
    hints: {
        type: Object,
        default: () => ({}),
    },
});
const emit = defineEmits(['submit', 'status', 'error']);
const state = reactive({ fields: [] });

const touched = computed(() => state.fields.some(f => f.touched));
const dirty = computed(() => state.fields.some(f => f.dirty));
const isValidating = computed(() => state.fields.some(f => f.isValidating));
const isEmpty = computed(() => state.fields.some(f => f.isEmpty));
const invalid = computed(() =>
    state.fields.some(f => f.invalid) || (isEmpty.value && dirty.value),
);
const valid = computed(() => !state.fields.some(f => f.invalid) && !(isEmpty.value && dirty.value));
const className = computed(() => ({
    'is-valid': valid.value,
    'is-invalid': invalid.value,
    'is-touched': touched.value,
    'is-untouched': !touched.value,
    'is-dirty': dirty.value,
    'is-pristine': !dirty.value,
    'is-validating': isValidating.value,
    'is-submitting': props.loading,
}));

provide('elForm', {
    ...toRefs(props),
    touched,
    untouched: computed(() => !touched.value),
    dirty,
    pristine: computed(() => !dirty.value),
    invalid,
    valid,
    isValidating,
    isSubmitting: computed(() => props.loading),
    className,
    // Getters keep these reactive: is recomputed into a new object whenever dynamic
    // field config changes, so a captured snapshot would make el-form-item validate stale rules.
    get model() { return props.model; },
    get rules() { return props.rules; },
    get hints() { return props.hints; },
    addField: (field) => state.fields.push(field),
    removeField: (field) => {
        const idx = state.fields.indexOf(field);
        if (idx !== -1) state.fields.splice(idx, 1);
    },
});

watch(touched, val => emit('status', { name: 'touched', value: val }), { immediate: true });
watch(dirty, val => emit('status', { name: 'dirty', value: val }), { immediate: true });
watch(invalid, val => emit('status', { name: 'invalid', value: val }), { immediate: true });
watch(valid, val => emit('status', { name: 'valid', value: val }), { immediate: true });
watch(isValidating, val => emit('status', { name: 'isValidating', value: val }), { immediate: true });
watch(className, val => emit('status', { name: 'className', value: val }), { immediate: true });

watch(() => props.rules, () => {
    if (!props.validateOnRuleChange) return;
    nextTick(() => validate(() => {}));
});

// Methods
function validate(callback) {
    if (!props.model) return;
    // eslint-disable-next-line @typescript-eslint/no-shadow
    let valid = true;
    let count = 0;

    if (typeof callback !== 'function' && window.Promise) {
        return new Promise((resolve, reject) => {
            validate((v) => v ? resolve(v) : reject(v));
        });
    }

    if (!state.fields.length) {
        callback(true);
        return;
    }

    state.fields.forEach(field => {
        field.validate('', errors => {
            if (errors) valid = false;
            if (++count === state.fields.length) callback(valid);
        }, true);
    });
}

function validateField(prop, cb) {
    const field = state.fields.find(f => f.prop === prop);
    if (!field) throw new Error('Must call validateField with valid prop!');
    field.validate('', cb);
}

function handleSubmit() {
    validate((v) => {
        if (v) {
            emit('submit', props.model);
        } else {
            emit('error');
        }
    });
}

function resetFields() {
    state.fields.forEach(f => f.resetField());
}

function clearValidate() {
    state.fields.forEach(f => f.clearValidate());
}

function resetInitialValue() {
    state.fields.forEach(f => f.resetInitialValue());
}

defineExpose({
    validate,
    validateField,
    fields: state.fields,
    resetFields,
    clearValidate,
    resetInitialValue,
});
</script>
